<template>
  <v-app class="bg-transparent" id="top-of-widget">
    <div ref="el" :style="{ 'min-height': '500px' }" class="d-flex justify-center align-center w-100">
      <v-progress-circular color="widget-color" v-if="loading.token || loading.product"
        indeterminate></v-progress-circular>

      <no-product
        v-else-if="(!loading.token || !loading.product) && (error == 'product-not-found' || error == 'product-not-active')"></no-product>

      <service-error v-else-if="(!loading.token || !loading.product) && error == 500"></service-error>

      <unkown-error v-else-if="(!loading.token || !loading.product) && error"></unkown-error>

      <div v-else-if="!loading.token && !loading.product && product">
        <v-card class="d-flex flex-wrap flex-md justify-space-between mb-2">
          <v-stepper :elevation="0" :flat="true" :items="stepper" :item-title="title" :item-value="value" hide-actions
            color="widget-color" v-model="step" :style="{
              maxWidth: '100vw'
            }">
          </v-stepper>

          <div class="text-caption text-grey d-flex flex-grow-1 ma-2 align-center">
            <div class="me-2">{{ $t("header.language") }}</div>
            <v-select v-model="$i18n.locale" :items="languageOptions" item-title="title" item-value="id" density
              hide-details variant="outlined">
              <template v-slot:selection="{ item }">
                <span class="text-caption">
                  {{ item.title }}
                </span>
              </template>

              <template v-slot:item="{ item, props }">
                <v-list-item density="compact" v-bind="props">
                  <template v-slot:title>
                    <v-list-item-title class="text-caption">
                      {{ item.title }}
                    </v-list-item-title>
                  </template>
                </v-list-item>
              </template>
            </v-select>
          </div>
        </v-card>

        <v-stepper-window :touch="false" v-model="step">
          <v-stepper-window-item :value="1">
            <product-preview></product-preview>
          </v-stepper-window-item>

          <v-stepper-window-item :value="2">
            <customer-information v-if="step == 2"></customer-information>
          </v-stepper-window-item>

          <v-stepper-window-item :value="3">
            <payment-product></payment-product>
          </v-stepper-window-item>

          <v-stepper-window-item :value="4">
            <payment-status :resultCode="status"></payment-status>
          </v-stepper-window-item>
        </v-stepper-window>
      </div>
    </div>
  </v-app>
</template>

<script>
import CustomerInformation from "@/components/CustomerInformation.vue";
import ProductPreview from "@/components/ProductPreview.vue";
import PaymentProduct from "@/components/PaymentProduct.vue";
import NoProduct from "@/components/error/NoProduct.vue";
import ServiceError from "@/components/error/ServiceError.vue";
import PaymentStatus from "@/components/PaymentStatus.vue";
import UnkownError from "@/components/error/UnkownError.vue";
import { isUpcoming, isExpired } from "@/utils/dateFormatter";
import { useElementSize } from "@vueuse/core";
import { ref } from "vue";
import moment from "moment-timezone";

export default {
  setup() {
    const el = ref(null);
    const { width, height } = useElementSize(el);

    return {
      el,
      width,
      height,
    };
  },
  name: "App",
  components: {
    ProductPreview,
    CustomerInformation,
    PaymentProduct,
    NoProduct,
    ServiceError,
    UnkownError,
    PaymentStatus,
  },
  data() {
    return {
      loading: {
        token: true,
        product: true
      },
      product: {},
      step: this.$store.state.step,
      error: false,
      payment: 1,
      languageOptions: [
        {
          title: "Indonesia",
          id: "id",
        },
        {
          title: "English",
          id: "en",
        },
      ],
    };
  },

  computed: {
    currentStep() {
      return this.$store.state.step;
    },
    status() {
      return this.$store.state.paymentStatus;
    },
    stepper() {
      return [
        {
          title: this.$t("header.step.choose-ticket"),
          value: 1,
        },
        {
          title: this.$t("header.step.fill-data"),
          value: 2,
        },
        {
          title: this.$t("header.step.pay"),
          value: 3,
        },
        {
          title: this.$t("header.step.done"),
          value: 4,
        },
      ];
    },
  },

  watch: {
    currentStep(value) {
      this.step = value;
    },
    height(value) {
      window.parent.postMessage({ type: "iframeHeight", height: value }, "*");
    },
    "$i18n.locale": {
      handler(value) {
        this.$moment.locale(value);
      },
    },
    status(value) {
      if (value.length !== 0 && value !== "01") {
        this.step = 4;
      }
    },
    step: {
      deep: true,
      handler(oldValue, newValue) {
        if (oldValue !== newValue) this.scrollToTop()
      }
    }
  },

  async created() {
    const href = window.location.href;
    var url = new URL(href);
    var productSlug = url.searchParams.get("product");
    var storeSlug = url.searchParams.get("store");
    var language = url.searchParams.get("lan") || "id";
    var appId = url.searchParams.get("appId");
    this.$store.state.appId = appId

    this.$i18n.locale = language;
    this.$moment.locale(language);

    this.productSlug = productSlug;

    var color = url.searchParams.get("color");
    this.$vuetify.theme.themes.customTheme.colors["widget-color"] = color
      ? `#${color}`
      : this.$vuetify.theme.themes.customTheme.colors.primary;

    const getToken = await this.axios
      .post("auth/guest")
      .then((response) => {
        const guestToken = response.data.response.guest_token;
        return guestToken;
      })
      .catch((error) => {
        const statusCode = error.response?.data.status_code;
        if (statusCode) {
          this.error = statusCode;
        }

        this.error = true;
      })
      .finally(() => (this.loading.token = false));
    this.$store.state.guestToken = getToken;

    this.axios
      .get(`product/slug/${storeSlug}/${productSlug}/get`)
      .then(async (response) => {
        const product = response.data.response;
        this.product = product;
        const publishDate = product.product_publish_date;
        const pulloutDate = product.product_pull_out_date;
        product.is_upcoming = isUpcoming(publishDate);
        product.is_expired = isExpired(pulloutDate);

        const reqPlatformFeeGeneral = await this.axios.get(`platform-fee/global/overview?name=service-fee`)
        product.platform_fee_general = reqPlatformFeeGeneral.data.response

        const reqPlatformFeeStore = await this.axios.get(`platform-fee/store/${product.store_id}`)
        product.platform_fee_store = reqPlatformFeeStore.data.response

        const reqPlatformFeeCategory = await this.axios.get(`platform-fee/store/${product.store_id}/category/${product.product_category.productcategory_id}`)
        product.platform_fee_category = reqPlatformFeeCategory.data.response

        product.product_types.forEach((type) => {
          type.producttype_price = parseInt(type.producttype_price);
          type.platform_fee_general = product.platform_fee_general;
          type.platform_fee_store = product.platform_fee_store;
          type.platform_fee_category = product.platform_fee_category;
          type.validation_dates?.sort((a, b) => {
            return moment(a).isAfter(
              moment(b)
            )
              ? 1
              : -1;
          })
        });

        if (appId) {
          const resAppSetting = await this.axios.get(`product/appsettings/${appId}`)
          const appSetting = resAppSetting.data.response

          if (appSetting && appSetting.integrator.length > 0) {
            const integratorDetails = [];
            const paymentMethodsDetails = [];
            for (const integratorId of appSetting.integrator) {
              const resIntegrator = await this.axios.get(`payment/integrator/${integratorId}`);
              const integrator = resIntegrator.data.response;
              if (integrator.paymentmethod && Array.isArray(integrator.paymentmethod)) {
                integrator.paymentmethod.forEach(pm => {
                  if (!paymentMethodsDetails.some(existing => existing.paymentmethod_id === pm.paymentmethod_id)) {
                    paymentMethodsDetails.push(pm);
                  }
                });
              }
              integratorDetails.push(integrator);
            }

            this.$store.state.paymentMethodsIntegrator = paymentMethodsDetails
            this.$store.state.integrator = integratorDetails
          }

          if (appSetting && appSetting.product_id == product.product_id) {
            product.product_types = product.product_types.filter(pt => appSetting.producttype_id.includes(pt.producttype_id))
            this.$store.state.appSetting = appSetting
          }
        }
        this.$store.state.product = product;
      })
      .catch((error) => {
        const code = error.response?.data?.code

        switch (code) {
          case 'product-not-active':
          case 'product-not-found':
            this.error = code
            break;
          default:
            this.error = 500
            break;
        }

      })
      .finally(() => {
        setTimeout(() => {
          this.loading.product = false;
        }, 500);
      });
  },

  mounted() {
    const carts = JSON.parse(localStorage.getItem("previousCart"))

    if (carts) {
      this.$store.state.cart = carts
      localStorage.removeItem('previousCart')
    }
  },

  methods: {
    addToCart(productType, qty = 1) {
      this.$store.commit("addProductToCart", {
        productType: productType,
        qty: qty,
      });
    },

    removeFromCart(productType) {
      this.$store.commit("removeProductFromCart", {
        productType: productType,
      });
    },

    findProductInCart(productTypeID) {
      return this.$store.getters.findProductInCart(productTypeID);
    },

    scrollToTop() {
      const element = document.getElementById("top-of-widget");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    },
  },
};
</script>

<style lang="scss">
@use "./plugins/main.scss";

* {
  font-family: main.$body-font-family;
}

.v-application {
  [class*="text-"] {
    font-family: main.$body-font-family !important;
  }

  font-family: main.$body-font-family !important;
}
</style>

<style>
.sticky {
  position: sticky;
  top: 0;
}

.v-stepper-window {
  margin: 0 !important;
}
</style>
